import { useEffect } from "react";
// Type-only: erased at build time, so this never pulls the 642-icon Icons.json into a bundle by
// itself. The import specifier below MUST stay exactly "./iconRegistry" — the marketing-client
// Vite plugin (vite.config.ssr.ts) matches on that literal string to swap in the partial registry.
import type IconsJson from "./Icons.json";
import { getIcon, IS_PARTIAL_ICON_REGISTRY, requestFullIconRegistry, useIconRegistryVersion } from "./iconRegistry";

interface IconProps {
	name: keyof typeof IconsJson;
	fill?: string;
	stroke?: string;
	viewBox?: string;
	classname?: string;
	className?: string;
	size?: number;
	pathProps?: React.SVGProps<SVGPathElement>;
}

const Icon: React.FC<IconProps> = ({ name, classname = "", className = "", fill = "", size = 16, viewBox = "0 0 1024 1024", stroke, pathProps }) => {
	// Called unconditionally (before any early return) so the component stays subscribed to the
	// registry and re-renders once a lazily-fetched icon arrives. In the SPA/server/tenant (full
	// registry) the version never changes, so this never causes an extra render there.
	useIconRegistryVersion();
	const icon = getIcon(name) as any;
	// Only the marketing client's partial registry can have a name that isn't loaded yet — the
	// full registry (SPA/server/tenant) either has the name or it's a genuinely bad name, same as
	// before this split existed.
	const missing = !icon && IS_PARTIAL_ICON_REGISTRY;

	useEffect(() => {
		if (missing) requestFullIconRegistry();
	}, [missing]);

	if (missing) {
		// The server already rendered this icon's real <path> children — it always has the full
		// registry. During hydration React leaves a dangerouslySetInnerHTML element's existing DOM
		// children alone (it only ever compares/replaces on `__html` changing after that), so the
		// server-rendered icon stays visible instead of vanishing. Once requestFullIconRegistry()
		// resolves, `missing` goes false and this re-renders with the real <path> children.
		return (
			<svg
				viewBox={viewBox}
				className={classname || className}
				fill={fill}
				style={{ width: size, height: size }}
				{...(stroke && { stroke })}
				dangerouslySetInnerHTML={{ __html: "" }}
				suppressHydrationWarning
			/>
		);
	}

	return (
		<svg viewBox={icon?.viewBox || viewBox} className={classname || className} fill={fill} style={{ width: size, height: size }} {...(stroke && { stroke })}>
			{icon?.path?.map((path: string | ({ d: string; opacity?: number } & Record<string, unknown>)) => {
				const isOverride = typeof path !== "string";
				const d = isOverride ? path.d : path;
				const opacity = typeof path === "string" ? undefined : path.opacity;

				return (
					<path
						key={d}
						d={d}
						opacity={opacity}
						{...(stroke && { stroke })}
						{...(icon.extraConfig && { ...icon.extraConfig })}
						{...pathProps}
						{...(isOverride && path)}
					></path>
				);
			})}
		</svg>
	);
};

export default Icon;
